/**
 * Pure, React-free helpers for the employee-grouped timecard surface (Hours &
 * Timesheet redesign). Deterministic + side-effect-free so the ordering, the
 * per-employee "at a glance" totals, and the per-row visual layout (date bands,
 * continuous-shift chains, group gaps) can be unit-tested directly (web-app has no
 * vitest harness yet — factored out to stay testable, matching the Phase-4
 * `supervisorApproval` helpers).
 *
 * SECURITY / PII: only org-chart + timestamp fields the API already returns are
 * touched (employee id, department id, clock in/out). No PIN or extra PII.
 */

import type { TimeSession } from '@worklogix/shared';

/**
 * Two contiguous sessions are treated as one continuous shift split across
 * departments when the clock-out of the earlier lands on the clock-in of the
 * later. Real punches can differ by rounding / formatting, so we allow a small
 * tolerance rather than requiring byte-identical ISO strings — but keep it tight
 * so genuinely separate back-to-back shifts are NOT falsely joined.
 */
const SWITCH_TOLERANCE_MS = 60_000;

/** Number of distinct accent colors cycled across continuous-shift chains. */
export const CHAIN_COLOR_COUNT = 4;

/**
 * Per-department hour totals for an employee's "at a glance" header, split into
 * the pending (unapproved) and already-approved buckets. A department appears
 * only if it has hours in at least one bucket. Open sessions (no clock-out)
 * contribute 0 to both.
 */
export interface DeptTotal {
  departmentId: string;
  departmentName: string;
  unapprovedHours: number;
  approvedHours: number;
}

/** One employee's block: their rows (time-ordered) + the at-a-glance totals. */
export interface EmployeeBlock {
  employeeId: string;
  employeeName: string;
  sessions: TimeSession[];
  /** Per-department unapproved/approved split. */
  deptTotals: DeptTotal[];
  /** Sum of the unapproved hours across {@link deptTotals}. */
  totalUnapproved: number;
  /** Sum of the approved hours across {@link deptTotals}. */
  totalApproved: number;
}

/** Per-row visual layout within an employee block. */
export interface RowLayout {
  /** Alternating 0/1 background band; flips each time the calendar day changes. */
  dateBand: 0 | 1;
  /** Insert a gap above this row — it begins a new punch-group (not the first in the block). */
  gapBefore: boolean;
  /**
   * Accent-color index (0..{@link CHAIN_COLOR_COUNT}-1) for a continuous-shift
   * chain of 2+ rows; `null` for a standalone row (no accent).
   */
  chainColor: number | null;
  /** First row of a continuous chain. */
  chainStart: boolean;
  /** Last row of a continuous chain. */
  chainEnd: boolean;
  /** Human note for the "continuous" badge tooltip (empty when standalone). */
  linkNote: string;
}

/** Parse an ISO instant to epoch ms, or null when absent/unparseable. */
function toMs(iso: string | undefined): number | null {
  if (iso === undefined || iso === '') {
    return null;
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** Hours between two ISO instants (>= 0), or 0 when either is missing/invalid. */
function hoursBetween(inIso: string, outIso: string | undefined): number {
  const a = toMs(inIso);
  const b = toMs(outIso);
  if (a === null || b === null || b < a) {
    return 0;
  }
  return (b - a) / 3_600_000;
}

/**
 * Order sessions so each employee's rows are contiguous and chronological:
 * by employee display name, then clock-in, then id (stable tiebreak). Does not
 * mutate the input. `nameOf` resolves an employee id to its display label so
 * grouping follows what the reviewer actually sees.
 */
export function orderSessionsByEmployee(
  sessions: readonly TimeSession[],
  nameOf: (employeeId: string) => string
): TimeSession[] {
  return [...sessions].sort((a, b) => {
    const an = nameOf(a.employeeId);
    const bn = nameOf(b.employeeId);
    if (an !== bn) {
      return an < bn ? -1 : 1;
    }
    const at = toMs(a.clockInAt) ?? 0;
    const bt = toMs(b.clockInAt) ?? 0;
    if (at !== bt) {
      return at - bt;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/** True when session `a` hands straight off to `b`: same employee, same instant, different dept. */
function isDeptSwitch(a: TimeSession, b: TimeSession): boolean {
  if (a.employeeId !== b.employeeId || a.departmentId === b.departmentId) {
    return false;
  }
  const out = toMs(a.clockOutAt);
  const inn = toMs(b.clockInAt);
  if (out === null || inn === null) {
    return false;
  }
  const gap = inn - out;
  return gap >= 0 && gap <= SWITCH_TOLERANCE_MS;
}

/** True when a session has been approved (drives the unapproved/approved split). */
function isApproved(s: TimeSession): boolean {
  return s.approvedAt !== undefined && s.approvedAt !== '';
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Split an ALREADY employee-grouped, time-ordered list into per-employee blocks
 * with "at a glance" per-department hour totals, each broken into the pending
 * (unapproved) and already-approved buckets by the session's approval state.
 * Closed sessions contribute their hours to one bucket; open sessions contribute
 * nothing (no clock-out). Posted rows (History) are approved, so they land in the
 * approved bucket.
 */
export function groupSessionsByEmployee(
  ordered: readonly TimeSession[],
  nameOf: (employeeId: string) => string,
  deptNameOf: (departmentId: string) => string
): EmployeeBlock[] {
  const blocks: EmployeeBlock[] = [];
  let current: EmployeeBlock | null = null;
  const unapprovedByDept = new Map<string, number>();
  const approvedByDept = new Map<string, number>();

  const flush = (): void => {
    if (current === null) {
      return;
    }
    const deptIds = new Set([...unapprovedByDept.keys(), ...approvedByDept.keys()]);
    const totals: DeptTotal[] = [...deptIds]
      .map((departmentId) => ({
        departmentId,
        departmentName: deptNameOf(departmentId),
        unapprovedHours: round2(unapprovedByDept.get(departmentId) ?? 0),
        approvedHours: round2(approvedByDept.get(departmentId) ?? 0),
      }))
      .sort((a, b) => (a.departmentName < b.departmentName ? -1 : 1));
    current.deptTotals = totals;
    current.totalUnapproved = round2(totals.reduce((sum, t) => sum + t.unapprovedHours, 0));
    current.totalApproved = round2(totals.reduce((sum, t) => sum + t.approvedHours, 0));
    blocks.push(current);
  };

  for (const s of ordered) {
    if (current === null || current.employeeId !== s.employeeId) {
      flush();
      unapprovedByDept.clear();
      approvedByDept.clear();
      current = {
        employeeId: s.employeeId,
        employeeName: nameOf(s.employeeId),
        sessions: [],
        deptTotals: [],
        totalUnapproved: 0,
        totalApproved: 0,
      };
    }
    current.sessions.push(s);
    if (s.clockOutAt !== undefined) {
      const hours = hoursBetween(s.clockInAt, s.clockOutAt);
      const bucket = isApproved(s) ? approvedByDept : unapprovedByDept;
      bucket.set(s.departmentId, (bucket.get(s.departmentId) ?? 0) + hours);
    }
  }
  flush();
  return blocks;
}

/**
 * Build per-row visual layout for an ALREADY employee-grouped, time-ordered list.
 * Within each employee block: a date band alternates on every calendar-day change
 * (`dayKeyOf` supplies the display day so bands match the visible Day column);
 * contiguous dept-switch chains (2+ rows) get a cycling accent color and are
 * bracketed (`chainStart`/`chainEnd`); and a gap is flagged before each new
 * punch-group so distinct groups don't clump together.
 */
export function computeRowLayout(
  ordered: readonly TimeSession[],
  deptNameOf: (departmentId: string) => string,
  dayKeyOf: (s: TimeSession) => string
): Map<string, RowLayout> {
  const layout = new Map<string, RowLayout>();
  let band: 0 | 1 = 0;
  let prevDay: string | null = null;
  let chainCounter = 0; // increments per chain to cycle colors

  for (let i = 0; i < ordered.length; i += 1) {
    const cur = ordered[i];
    if (cur === undefined) {
      continue;
    }
    const prev = i > 0 ? ordered[i - 1] : undefined;
    const next = i + 1 < ordered.length ? ordered[i + 1] : undefined;

    const isBlockFirst = prev === undefined || prev.employeeId !== cur.employeeId;
    if (isBlockFirst) {
      band = 0;
      prevDay = null;
      // Start a fresh color cadence per employee so the first chain is color 0.
    }

    // Date band: flip whenever the display day changes within the block.
    const day = dayKeyOf(cur);
    if (prevDay !== null && day !== prevDay) {
      band = band === 0 ? 1 : 0;
    }
    prevDay = day;

    const linkedToPrev = prev !== undefined && isDeptSwitch(prev, cur);
    const linkedToNext = next !== undefined && isDeptSwitch(cur, next);

    // A chain starts when this row links forward but not backward.
    if (linkedToNext && !linkedToPrev) {
      chainCounter += 1;
    }
    const inChain = linkedToPrev || linkedToNext;
    const chainColor = inChain ? (chainCounter - 1 + CHAIN_COLOR_COUNT) % CHAIN_COLOR_COUNT : null;

    const notes: string[] = [];
    if (linkedToPrev && prev !== undefined) {
      notes.push(`Continues from ${deptNameOf(prev.departmentId)} (no gap)`);
    }
    if (linkedToNext && next !== undefined) {
      notes.push(`Continues into ${deptNameOf(next.departmentId)} (no gap)`);
    }

    layout.set(cur.id, {
      dateBand: band,
      // Gap before a new punch-group (standalone row or start of a chain), except
      // the very first row of the employee block.
      gapBefore: !isBlockFirst && !linkedToPrev,
      chainColor: inChain ? (chainColor ?? 0) : null,
      chainStart: linkedToNext && !linkedToPrev,
      chainEnd: linkedToPrev && !linkedToNext,
      linkNote: notes.join(' · '),
    });
  }
  return layout;
}
